/**
 * Assessment Engine (Phase 4) validation contracts.
 *
 * Mirrors the schema in `packages/db/src/schema/assessments.ts`. This module
 * is consumed by:
 *   - The API route handlers (input validation).
 *   - Three downstream feature modules (Hiring, Games, Interview Scripts)
 *     that each adopt a fixed `scope` value but reuse the same template /
 *     question / run / attempt / answer surface.
 *
 * Conventions:
 *   - All ids are UUID v7 strings (`idSchema`).
 *   - JSON columns are validated at the boundary; storage is text +
 *     `JSON.stringify`/`JSON.parse` at the app layer.
 *   - Strict objects — unknown keys throw a 400 with `validation_error`.
 *
 * Spec: docs/deep-research-report4.md §"Assessment Engine".
 * Roadmap: docs/implementation-plan-v2.md §9.4.
 */
import { z } from 'zod';
import { idSchema, paginationSchema } from './common.js';

// ----- Enums ---------------------------------------------------------------

export const assessmentScopeSchema = z.enum([
  'hiring',
  'rules',
  'weekly_quiz',
  'monthly_quiz',
  'interview_script',
]);
export type AssessmentScope = z.infer<typeof assessmentScopeSchema>;

export const assessmentTemplateStatusSchema = z.enum([
  'draft',
  'in_review',
  'published',
  'archived',
]);
export type AssessmentTemplateStatus = z.infer<typeof assessmentTemplateStatusSchema>;

export const assessmentModeSchema = z.enum(['timed_exam', 'practice']);
export type AssessmentMode = z.infer<typeof assessmentModeSchema>;

export const assessmentOwnerTypeSchema = z.enum(['global', 'course', 'batch']);
export type AssessmentOwnerType = z.infer<typeof assessmentOwnerTypeSchema>;

export const assessmentSelectionStrategySchema = z.enum(['random', 'fixed']);
export const assessmentOrderingStrategySchema = z.enum(['shuffle', 'as_authored']);

export const assessmentDifficultySchema = z.enum(['easy', 'medium', 'hard']);

export const assessmentRunStatusSchema = z.enum([
  'scheduled',
  'open',
  'closed',
  'cancelled',
]);
export type AssessmentRunStatus = z.infer<typeof assessmentRunStatusSchema>;

export const assessmentAudienceTypeSchema = z.enum([
  'candidate_list',
  'batch',
  'selected_users',
]);
export type AssessmentAudienceType = z.infer<typeof assessmentAudienceTypeSchema>;

export const assessmentAttemptStatusSchema = z.enum([
  'in_progress',
  'submitted',
  'expired',
  'void',
]);
export type AssessmentAttemptStatus = z.infer<typeof assessmentAttemptStatusSchema>;

export const assessmentAnswerStatusSchema = z.enum([
  'unanswered',
  'answered',
  'finalized',
]);
export type AssessmentAnswerStatus = z.infer<typeof assessmentAnswerStatusSchema>;

// ----- Common scalars -----------------------------------------------------

const titleSchema = z.string().trim().min(2).max(200);
const descriptionSchema = z.string().trim().min(1).max(4000);
const tagSchema = z.string().trim().min(1).max(64);
const periodKeySchema = z
  .string()
  .trim()
  .min(4)
  .max(40)
  .regex(/^[A-Za-z0-9_-]+$/u, 'Period key must be alphanumeric, dash, or underscore');
const isoTimestampSchema = z.string().datetime({ offset: true });

// ----- Tag rules ----------------------------------------------------------

export const tagRuleSchema = z
  .object({
    tag: tagSchema,
    count: z.number().int().min(1).max(500),
  })
  .strict();

export const tagRulesSchema = z.array(tagRuleSchema).min(1).max(50);
export type TagRule = z.infer<typeof tagRuleSchema>;
export type TagRules = z.infer<typeof tagRulesSchema>;

// ----- Question options ---------------------------------------------------

export const questionOptionSchema = z
  .object({
    id: z.string().trim().min(1).max(64),
    label: z.string().trim().min(1).max(2000),
  })
  .strict();
export type QuestionOption = z.infer<typeof questionOptionSchema>;

export const questionOptionsArraySchema = z
  .array(questionOptionSchema)
  .min(2, 'A question needs at least two options')
  .max(20, 'Too many options')
  .refine((opts) => new Set(opts.map((o) => o.id)).size === opts.length, {
    message: 'Option ids must be unique',
  });

// ----- Templates ----------------------------------------------------------

/**
 * L-4: scopes whose templates must be single-attempt. The hiring aptitude
 * test (MODULE 1 §2) and the rules-and-regulations test (MODULE 1 §6) are
 * gating exams — a candidate's pass/fail is what advances the application
 * through the pipeline, so the product intent is "one attempt, one verdict".
 * Allowing a retry would turn a gating exam into a practice quiz.
 *
 * Quiz scopes (weekly/monthly) and interview_script practice legitimately
 * allow multiple attempts, so the constraint is deliberately scope-limited.
 *
 * Exported so the route handlers can reuse the same source-of-truth set
 * for their PATCH / publish / run-create guards (the update schema and the
 * run-create body don't carry the template scope, so those checks live at
 * the route boundary where the persisted scope is known).
 */
export const SINGLE_ATTEMPT_SCOPES: ReadonlySet<AssessmentScope> = new Set<
  AssessmentScope
>(['hiring', 'rules']);

export const templateCreateSchema = z
  .object({
    scope: assessmentScopeSchema,
    title: titleSchema,
    description: descriptionSchema.optional(),
    ownerType: assessmentOwnerTypeSchema.default('global'),
    ownerRefId: idSchema.nullable().optional(),
    mode: assessmentModeSchema,
    /** Required when `mode='timed_exam'` — checked by route-level refine. */
    durationSeconds: z.number().int().min(30).max(8 * 60 * 60).optional(),
    questionDrawCount: z.number().int().min(1).max(500),
    selectionStrategy: assessmentSelectionStrategySchema.default('random'),
    orderingStrategy: assessmentOrderingStrategySchema.default('shuffle'),
    tagRules: tagRulesSchema.optional(),
    passThresholdPercent: z.number().int().min(0).max(100).optional(),
    maxAttemptsPerUser: z.number().int().min(1).max(20).default(1),
    showScoreToTaker: z.boolean().default(true),
    showAnswerReviewToTaker: z.boolean().default(false),
    antiCheatProfile: z.string().trim().min(1).max(64).nullable().optional(),
  })
  .strict()
  .refine(
    (data) => data.mode !== 'timed_exam' || data.durationSeconds !== undefined,
    {
      message: 'durationSeconds is required when mode is timed_exam',
      path: ['durationSeconds'],
    },
  )
  .refine(
    (data) =>
      data.tagRules === undefined ||
      data.tagRules.reduce((acc, r) => acc + r.count, 0) ===
        data.questionDrawCount,
    {
      message: 'Sum of tagRules.count must equal questionDrawCount',
      path: ['tagRules'],
    },
  )
  .refine(
    (data) => data.ownerType === 'global' || data.ownerRefId !== undefined,
    {
      message: 'ownerRefId is required when ownerType is course or batch',
      path: ['ownerRefId'],
    },
  )
  .refine(
    // M-7: hiring and rules tests must show every candidate the SAME fixed
    // set of questions (order may still vary per candidate via
    // orderingStrategy='shuffle'). Random per-attempt selection would draw a
    // different subset for each candidate when the active pool is larger than
    // questionDrawCount, making scores incomparable. The publish handler
    // re-checks this so legacy draft rows can't go live with the wrong policy.
    (data) =>
      (data.scope !== 'hiring' && data.scope !== 'rules') ||
      data.selectionStrategy === 'fixed',
    {
      message:
        "Hiring and rules templates must use selectionStrategy='fixed' so every candidate sees the same question set.",
      path: ['selectionStrategy'],
    },
  )
  .refine(
    // L-4: hiring and rules tests are single-verdict gating exams (MODULE 1
    // §2, §6 — "Must pass before joining"). Refuse any value other than 1.
    // The route handlers re-check this on PATCH / publish / run-create so
    // legacy draft rows and run-level overrides can't reintroduce the gap.
    (data) =>
      !SINGLE_ATTEMPT_SCOPES.has(data.scope) || data.maxAttemptsPerUser === 1,
    {
      message:
        'Hiring and rules templates must be single-attempt (maxAttemptsPerUser=1). These are gating exams under MODULE 1 §2 and §6.',
      path: ['maxAttemptsPerUser'],
    },
  );

export const templateUpdateSchema = z
  .object({
    title: titleSchema.optional(),
    description: descriptionSchema.nullable().optional(),
    ownerType: assessmentOwnerTypeSchema.optional(),
    ownerRefId: idSchema.nullable().optional(),
    mode: assessmentModeSchema.optional(),
    durationSeconds: z.number().int().min(30).max(8 * 60 * 60).nullable().optional(),
    questionDrawCount: z.number().int().min(1).max(500).optional(),
    selectionStrategy: assessmentSelectionStrategySchema.optional(),
    orderingStrategy: assessmentOrderingStrategySchema.optional(),
    tagRules: tagRulesSchema.nullable().optional(),
    passThresholdPercent: z.number().int().min(0).max(100).nullable().optional(),
    maxAttemptsPerUser: z.number().int().min(1).max(20).optional(),
    showScoreToTaker: z.boolean().optional(),
    showAnswerReviewToTaker: z.boolean().optional(),
    antiCheatProfile: z.string().trim().min(1).max(64).nullable().optional(),
  })
  .strict();

/**
 * Query contract for `GET /api/assessments/templates`.
 *
 * `scope`, `status`, `mode`, and `ownerType` are DELIBERATELY ABSENT from
 * this schema. They are now comma-separated multi-select facets
 * (`?status=draft,published`, `?scope=hiring,rules`, `?mode=timed_exam`,
 * `?ownerType=global,course`); a comma value would fail a single-enum parse
 * with a 400. Following the claims / probation-queue precedent, the route
 * reads those params straight off `url.searchParams` and validates them with
 * `parseEnumListFilter` (which drops unknown tokens safely). Because
 * `paginationSchema` is `.strict()`, listing those keys here would 400 on
 * the multi-value input, so they must NOT appear in this schema.
 *
 * The frontend still passes a SINGLE `scope` from its URL prefilter — a
 * single value is a degenerate comma-list of one, so it flows through
 * `parseEnumListFilter` unchanged and continues to work.
 *
 * `createdFrom`/`createdTo` (date range) and `sort` are also parsed off
 * `url.searchParams` in the route (`parseDateOnly` / `parseSort`). Only the
 * remaining single-value params stay in this schema.
 */
export const templateListQuerySchema = paginationSchema.extend({
  ownerRefId: idSchema.optional(),
  templateKey: z.string().trim().min(1).max(120).optional(),
  q: z.string().trim().min(1).max(120).optional(),
});

// ----- Questions ----------------------------------------------------------

export const questionCreateSchema = z
  .object({
    stem: z.string().trim().min(2).max(8000),
    questionType: z.literal('single_choice').default('single_choice'),
    options: questionOptionsArraySchema,
    correctOptionIds: z.array(z.string().trim().min(1).max(64)).min(1).max(20),
    explanation: z.string().trim().min(1).max(4000).nullable().optional(),
    primaryTag: tagSchema.nullable().optional(),
    tags: z.array(tagSchema).max(20).optional(),
    difficulty: assessmentDifficultySchema.nullable().optional(),
    points: z.number().int().min(1).max(1000).default(1),
    position: z.number().int().min(1).max(10_000).nullable().optional(),
    shuffleOptions: z.boolean().default(true),
  })
  .strict()
  .refine(
    (data) => {
      const ids = new Set(data.options.map((o) => o.id));
      return data.correctOptionIds.every((id) => ids.has(id));
    },
    {
      message: 'correctOptionIds must reference existing option ids',
      path: ['correctOptionIds'],
    },
  )
  .refine(
    (data) => new Set(data.correctOptionIds).size === data.correctOptionIds.length,
    {
      message: 'correctOptionIds must be unique',
      path: ['correctOptionIds'],
    },
  );

export const questionUpdateSchema = z
  .object({
    stem: z.string().trim().min(2).max(8000).optional(),
    options: questionOptionsArraySchema.optional(),
    correctOptionIds: z
      .array(z.string().trim().min(1).max(64))
      .min(1)
      .max(20)
      .optional(),
    explanation: z.string().trim().min(1).max(4000).nullable().optional(),
    primaryTag: tagSchema.nullable().optional(),
    tags: z.array(tagSchema).max(20).nullable().optional(),
    difficulty: assessmentDifficultySchema.nullable().optional(),
    points: z.number().int().min(1).max(1000).optional(),
    position: z.number().int().min(1).max(10_000).nullable().optional(),
    shuffleOptions: z.boolean().optional(),
  })
  .strict()
  .refine(
    (data) => {
      if (!data.options || !data.correctOptionIds) return true;
      const ids = new Set(data.options.map((o) => o.id));
      return data.correctOptionIds.every((id) => ids.has(id));
    },
    {
      message: 'correctOptionIds must reference existing option ids',
      path: ['correctOptionIds'],
    },
  );

// ----- Runs ---------------------------------------------------------------

export const eligibilityShapeSchema = z
  .object({
    userIds: z.array(idSchema).optional(),
    candidateIds: z.array(idSchema).optional(),
    batchId: idSchema.optional(),
    includeInactive: z.boolean().optional(),
  })
  .strict();
export type AssessmentEligibility = z.infer<typeof eligibilityShapeSchema>;

export const runCreateSchema = z
  .object({
    templateId: idSchema,
    title: titleSchema,
    audienceType: assessmentAudienceTypeSchema,
    audienceRefId: idSchema.nullable().optional(),
    periodKey: periodKeySchema.nullable().optional(),
    eligibility: eligibilityShapeSchema.nullable().optional(),
    opensAt: isoTimestampSchema.nullable().optional(),
    closesAt: isoTimestampSchema.nullable().optional(),
    passThresholdPercent: z.number().int().min(0).max(100).nullable().optional(),
    maxAttemptsPerUser: z.number().int().min(1).max(20).nullable().optional(),
  })
  .strict()
  .refine(
    (data) =>
      !data.opensAt || !data.closesAt || data.opensAt <= data.closesAt,
    { message: 'opensAt cannot be after closesAt', path: ['opensAt'] },
  )
  .refine(
    (data) =>
      data.audienceType !== 'batch' || data.audienceRefId !== undefined,
    {
      message: 'audienceRefId is required when audienceType is batch',
      path: ['audienceRefId'],
    },
  );

/**
 * Body for `PATCH /api/assessments/runs/:id`.
 *
 * Two operational flavours share one endpoint:
 *   1. Eligibility update — only valid when the run's `audienceType` is
 *      `selected_users`. Pass `audienceUserIds` to overwrite the run's
 *      allow-list verbatim.
 *   2. Reschedule — any of `opensAt`/`closesAt`/`passThresholdPercent`/
 *      `maxAttemptsPerUser`.
 *
 * The two are validated together (rather than as a tagged union) so a
 * caller may legitimately combine them in one round-trip. The route
 * enforces the semantic guards (audience-type gating, status gating, and
 * opensAt < closesAt against merged values).
 */
export const runUpdateSchema = z
  .object({
    audienceUserIds: z.array(idSchema).min(1).optional(),
    opensAt: isoTimestampSchema.nullable().optional(),
    closesAt: isoTimestampSchema.nullable().optional(),
    passThresholdPercent: z.number().int().min(0).max(100).nullable().optional(),
    maxAttemptsPerUser: z.number().int().min(1).max(20).nullable().optional(),
  })
  .strict()
  .refine(
    (data) =>
      data.audienceUserIds !== undefined ||
      data.opensAt !== undefined ||
      data.closesAt !== undefined ||
      data.passThresholdPercent !== undefined ||
      data.maxAttemptsPerUser !== undefined,
    { message: 'No fields to update' },
  );

/**
 * Body for `POST /api/assessments/attempts/:id/flag`. Records a manual
 * review marker (severity `info` is "look at this", `warn` is "looks
 * suspicious"). Independent of the auto-set `flagged_for_review` bit
 * raised by tab-blur heuristics.
 */
export const attemptFlagSchema = z
  .object({
    reason: z.string().trim().min(2).max(1000),
    severity: z.enum(['info', 'warn']).default('info'),
  })
  .strict();

/**
 * Body for `POST /api/assessments/attempts/:id/void`. Voiding an attempt
 * excludes it from run aggregates and disqualifies any score it carried.
 */
export const attemptVoidSchema = z
  .object({
    reason: z.string().trim().min(2).max(1000),
  })
  .strict();

export const runListQuerySchema = paginationSchema.extend({
  templateId: idSchema.optional(),
  status: assessmentRunStatusSchema.optional(),
  audienceType: assessmentAudienceTypeSchema.optional(),
  audienceRefId: idSchema.optional(),
  periodKey: periodKeySchema.optional(),
  opensAfter: isoTimestampSchema.optional(),
  opensBefore: isoTimestampSchema.optional(),
});

// ----- Attempts -----------------------------------------------------------

export const answerSubmitSchema = z
  .object({
    questionId: idSchema,
    selectedOptionIds: z.array(z.string().trim().min(1).max(64)).min(1).max(20),
  })
  .strict()
  .refine(
    (data) =>
      new Set(data.selectedOptionIds).size === data.selectedOptionIds.length,
    {
      message: 'selectedOptionIds must be unique',
      path: ['selectedOptionIds'],
    },
  );

export const blurEventSchema = z
  .object({
    kind: z
      .enum(['tab_blur', 'visibility_hidden', 'window_blur', 'paste', 'devtools'])
      .default('tab_blur'),
    occurredAt: isoTimestampSchema.optional(),
  })
  .strict();

export const heartbeatSchema = z
  .object({
    /** Optional client-side telemetry; we don't trust it for timing. */
    clientEventCount: z.number().int().min(0).max(1_000_000).optional(),
  })
  .strict()
  .or(z.undefined());

export const attemptsListQuerySchema = paginationSchema.extend({
  runId: idSchema.optional(),
  status: assessmentAttemptStatusSchema.optional(),
});

// ----- Response shapes ----------------------------------------------------

export const templateResponseSchema = z.object({
  id: z.string(),
  templateKey: z.string(),
  scope: assessmentScopeSchema,
  title: z.string(),
  description: z.string().nullable(),
  ownerType: assessmentOwnerTypeSchema,
  ownerRefId: z.string().nullable(),
  mode: assessmentModeSchema,
  status: assessmentTemplateStatusSchema,
  durationSeconds: z.number().int().nullable(),
  questionDrawCount: z.number().int(),
  selectionStrategy: assessmentSelectionStrategySchema,
  orderingStrategy: assessmentOrderingStrategySchema,
  tagRules: tagRulesSchema.nullable(),
  passThresholdPercent: z.number().int().nullable(),
  maxAttemptsPerUser: z.number().int(),
  showScoreToTaker: z.boolean(),
  showAnswerReviewToTaker: z.boolean(),
  antiCheatProfile: z.string().nullable(),
  version: z.number().int(),
  supersedesTemplateId: z.string().nullable(),
  publishedAt: z.string().nullable(),
  publishedBy: z.string().nullable(),
  createdBy: z.string(),
  updatedBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const questionResponseSchema = z.object({
  id: z.string(),
  templateId: z.string(),
  questionKey: z.string(),
  position: z.number().int().nullable(),
  stem: z.string(),
  questionType: z.string(),
  options: questionOptionsArraySchema,
  correctOptionIds: z.array(z.string()),
  explanation: z.string().nullable(),
  primaryTag: z.string().nullable(),
  tags: z.array(z.string()).nullable(),
  difficulty: assessmentDifficultySchema.nullable(),
  points: z.number().int(),
  shuffleOptions: z.boolean(),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

/**
 * Alias kept for consumers that prefer the fully-qualified
 * `assessmentQuestionResponseSchema` name (mirrors
 * `assessmentTemplateStatusSchema` et al). The shape is identical to
 * `questionResponseSchema`.
 */
export const assessmentQuestionResponseSchema = questionResponseSchema;

/**
 * Response shape for `GET /api/assessments/templates/:id/questions`.
 * The list is unpaginated — per-template question counts are bounded by
 * `questionDrawCount`-driven pool sizes (tens, not thousands) and the UI
 * needs all rows at once to render the authoring table.
 */
export const templateQuestionListResponseSchema = z.object({
  items: z.array(assessmentQuestionResponseSchema),
});

/**
 * Query string for the same endpoint. `activeOnly=true` filters out
 * soft-deleted questions; the default keeps them (rendered strikethrough
 * in the UI so authors can recover from mistakes).
 */
export const templateQuestionListQuerySchema = z
  .object({
    activeOnly: z
      .union([z.literal('true'), z.literal('false'), z.literal('1'), z.literal('0')])
      .optional(),
  })
  .strict();

export const runResponseSchema = z.object({
  id: z.string(),
  templateId: z.string(),
  title: z.string(),
  audienceType: assessmentAudienceTypeSchema,
  audienceRefId: z.string().nullable(),
  periodKey: z.string().nullable(),
  eligibility: eligibilityShapeSchema.nullable(),
  poolQuestionIds: z.array(z.string()).nullable(),
  status: assessmentRunStatusSchema,
  opensAt: z.string().nullable(),
  closesAt: z.string().nullable(),
  passThresholdPercent: z.number().int().nullable(),
  maxAttemptsPerUser: z.number().int().nullable(),
  publishedAt: z.string().nullable(),
  closedAt: z.string().nullable(),
  createdBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const attemptStartQuestionSchema = z.object({
  id: z.string(),
  position: z.number().int(),
  stem: z.string(),
  questionType: z.string(),
  options: questionOptionsArraySchema,
  points: z.number().int(),
});

export const attemptStartResponseSchema = z.object({
  attemptId: z.string(),
  runId: z.string(),
  templateId: z.string(),
  mode: assessmentModeSchema,
  status: assessmentAttemptStatusSchema,
  startedAt: z.string(),
  expiresAt: z.string().nullable(),
  secondsRemaining: z.number().int().nullable(),
  questions: z.array(attemptStartQuestionSchema),
});

export const attemptHeartbeatResponseSchema = z.object({
  status: assessmentAttemptStatusSchema,
  secondsRemaining: z.number().int(),
  expiresAt: z.string().nullable(),
});

export const attemptCompleteResponseSchema = z.object({
  status: assessmentAttemptStatusSchema,
  scoreRaw: z.number().int().nullable(),
  scorePercent: z.number().int().nullable(),
  correctCount: z.number().int().nullable(),
  answeredCount: z.number().int().nullable(),
  pass: z.boolean().nullable(),
  autoSubmitted: z.boolean(),
  submittedAt: z.string().nullable(),
});

export const answerSubmitResponseSchema = z.object({
  questionId: z.string(),
  attemptCount: z.number().int(),
  isCorrect: z.boolean().nullable(),
  pointsAwarded: z.number().int().nullable(),
  status: assessmentAnswerStatusSchema,
  retryAvailable: z.boolean(),
});

// ----- Type exports -------------------------------------------------------

export type TemplateCreateInput = z.infer<typeof templateCreateSchema>;
export type TemplateUpdateInput = z.infer<typeof templateUpdateSchema>;
export type TemplateListQuery = z.infer<typeof templateListQuerySchema>;
export type QuestionCreateInput = z.infer<typeof questionCreateSchema>;
export type QuestionUpdateInput = z.infer<typeof questionUpdateSchema>;
export type RunCreateInput = z.infer<typeof runCreateSchema>;
export type RunUpdateInput = z.infer<typeof runUpdateSchema>;
export type RunListQuery = z.infer<typeof runListQuerySchema>;
export type AttemptFlagInput = z.infer<typeof attemptFlagSchema>;
export type AttemptVoidInput = z.infer<typeof attemptVoidSchema>;
export type AnswerSubmitInput = z.infer<typeof answerSubmitSchema>;
export type BlurEventInput = z.infer<typeof blurEventSchema>;
export type AttemptsListQuery = z.infer<typeof attemptsListQuerySchema>;
export type TemplateResponse = z.infer<typeof templateResponseSchema>;
export type QuestionResponse = z.infer<typeof questionResponseSchema>;
export type AssessmentQuestionResponse = z.infer<typeof assessmentQuestionResponseSchema>;
export type TemplateQuestionListResponse = z.infer<typeof templateQuestionListResponseSchema>;
export type TemplateQuestionListQuery = z.infer<typeof templateQuestionListQuerySchema>;
export type RunResponse = z.infer<typeof runResponseSchema>;
export type AttemptStartResponse = z.infer<typeof attemptStartResponseSchema>;
export type AttemptHeartbeatResponse = z.infer<typeof attemptHeartbeatResponseSchema>;
export type AttemptCompleteResponse = z.infer<typeof attemptCompleteResponseSchema>;
export type AnswerSubmitResponse = z.infer<typeof answerSubmitResponseSchema>;
